import { useEffect, useRef, useState } from "react";
import { Link, useParams } from "react-router-dom";
import axios from "axios";
import { API, publicMediaUrl } from "../lib/api";
import { Play, Pause, Film, Loader2 } from "lucide-react";

export default function Share() {
  const { slug } = useParams();
  const audioRef = useRef(null);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [playing, setPlaying] = useState(false);
  const [chapter, setChapter] = useState(0);
  const [progress, setProgress] = useState(0);
  const [error, setError] = useState(null);

  useEffect(() => {
    axios.get(`${API}/share/${slug}`).then(({ data }) => setData(data))
      .catch(() => setError("Trailer not found"))
      .finally(() => setLoading(false));
  }, [slug]);

  useEffect(() => {
    const a = audioRef.current;
    if (!a || !data) return;
    const onTime = () => {
      const p = (a.currentTime / (a.duration || 1));
      setProgress(p);
      const total = data.episode.media_ids?.length || 1;
      setChapter(Math.min(total - 1, Math.floor(p * total)));
    };
    const onEnd = () => setPlaying(false);
    a.addEventListener("timeupdate", onTime);
    a.addEventListener("ended", onEnd);
    return () => {
      a.removeEventListener("timeupdate", onTime);
      a.removeEventListener("ended", onEnd);
    };
  }, [data]);

  const toggle = () => {
    const a = audioRef.current;
    if (!a) return;
    if (playing) { a.pause(); setPlaying(false); }
    else { a.play(); setPlaying(true); }
  };

  if (loading) return (
    <div className="min-h-screen bg-[#050505] text-white flex items-center justify-center">
      <Loader2 className="w-8 h-8 animate-spin text-[#FF2A2A]" />
    </div>
  );

  if (error || !data) return (
    <div className="min-h-screen bg-[#050505] text-white flex items-center justify-center px-6 text-center">
      <div>
        <h1 className="font-display font-black text-4xl tracking-tighter">Trailer not found.</h1>
        <Link to="/" className="mt-6 inline-block font-mono text-xs uppercase tracking-widest text-[#FF2A2A]">
          Make your own →
        </Link>
      </div>
    </div>
  );

  const { episode, author } = data;
  const script = episode.script || {};
  const chapters = script.chapters || [];
  const mediaIds = episode.media_ids || [];
  const currentMediaId = mediaIds[chapter] || mediaIds[0];
  const currentNarration = chapters[chapter % Math.max(chapters.length, 1)]?.narration || script.intro || "";

  return (
    <div className="min-h-screen bg-[#050505] text-white relative overflow-hidden" data-testid="share-page">
      {/* Background blur */}
      {currentMediaId && (
        <img
          src={publicMediaUrl(slug, currentMediaId)}
          alt=""
          className="fixed inset-0 w-full h-full object-cover opacity-30 blur-3xl scale-110"
        />
      )}
      <div className="fixed inset-0 bg-[#050505]/70" />

      <div className="relative z-10 min-h-screen flex flex-col">
        {/* Top brand */}
        <div className="px-6 sm:px-12 py-6 flex items-center justify-between">
          <Link to="/" data-testid="share-logo" className="flex items-center gap-2">
            <Film className="w-5 h-5 text-[#FF2A2A]" />
            <span className="font-display font-black tracking-tighter text-xl">
              LIFE<span className="text-[#FF2A2A]">REPLAY</span>
            </span>
          </Link>
          <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-white/40 hidden sm:block">
            Created by {author.name}
          </div>
        </div>

        <div className="flex-1 flex items-center justify-center px-6 sm:px-12 py-12">
          <div className="w-full max-w-5xl">
            {/* Cinema frame */}
            <div className="relative aspect-[16/10] bg-black overflow-hidden border border-white/10 poster-shadow">
              {currentMediaId && (
                <img
                  key={currentMediaId}
                  src={publicMediaUrl(slug, currentMediaId)}
                  alt=""
                  className="absolute inset-0 w-full h-full object-cover cinema-fade-in"
                />
              )}
              <div className="absolute inset-0 bg-gradient-to-t from-black/95 via-black/30 to-black/40" />

              <div className="absolute top-6 left-6 font-mono text-[10px] uppercase tracking-[0.4em] text-white/80 bg-black/60 backdrop-blur px-3 py-1.5">
                {episode.vibe}
              </div>

              <div className="absolute bottom-20 left-0 right-0 text-center px-8">
                <p className="font-emotional text-2xl sm:text-4xl text-white leading-snug max-w-4xl mx-auto drop-shadow-[0_2px_20px_rgba(0,0,0,0.9)]">
                  {`"${currentNarration}"`}
                </p>
              </div>

              <div className="absolute bottom-0 left-0 right-0 h-1 bg-white/10">
                <div className="h-full bg-[#FF2A2A] transition-all duration-200" style={{ width: `${progress * 100}%` }} />
              </div>

              <button
                data-testid="share-play-button"
                onClick={toggle}
                className="absolute inset-0 m-auto w-20 h-20 rounded-full bg-[#FF2A2A]/90 hover:bg-[#FF2A2A] flex items-center justify-center transition-all hover:scale-110"
                style={{ display: playing ? "none" : "flex" }}
              >
                <Play className="w-9 h-9 fill-white text-white ml-1" />
              </button>
              {playing && (
                <button
                  data-testid="share-pause-button"
                  onClick={toggle}
                  className="absolute bottom-6 right-6 w-12 h-12 rounded-full bg-black/60 hover:bg-black backdrop-blur flex items-center justify-center transition-all"
                >
                  <Pause className="w-5 h-5 fill-white text-white" />
                </button>
              )}

              {episode.audio_file_id && (
                <audio ref={audioRef} src={publicMediaUrl(slug, episode.audio_file_id)} preload="auto" />
              )}
            </div>

            {/* Title + CTA */}
            <div className="mt-12 text-center">
              <div className="font-mono text-xs uppercase tracking-[0.3em] text-[#FF2A2A]">
                A {episode.vibe.toUpperCase()} TRAILER BY {author.name?.toUpperCase()}
              </div>
              <h1 className="font-display font-black text-5xl sm:text-7xl tracking-tighter leading-none mt-4">
                {episode.title}
              </h1>
              {script.tagline && (
                <p className="font-emotional text-2xl text-white/60 mt-6">{`"${script.tagline}"`}</p>
              )}

              <Link
                to="/login"
                data-testid="share-make-yours-cta"
                className="inline-flex items-center gap-3 bg-[#FF2A2A] hover:bg-[#FF5555] text-white font-mono uppercase tracking-widest text-sm px-10 py-5 mt-12 transition-colors"
              >
                <Play className="w-4 h-4 fill-white" />
                Make yours
              </Link>
              <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-white/40 mt-6">
                Created with LifeReplay · lifereplay.app
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
